/**
 * CancelRunButton — the Run Board's one run-level mutation control.
 *
 * Props in, one side effect out: after an explicit confirm it fires
 * `cancel(runId)`, disables itself in-flight, optimistically recedes on success
 * (and asks the parent to re-fetch the board), and on failure reverts to an
 * actionable state showing the honest error the client named. Purely local UI
 * state — no polling, no global reads; the parent owns the data and gates
 * rendering on a live run, so this never shows as a primary action on a
 * clearly-terminal run. (The daemon cancel is idempotent, so a stray click would
 * be a safe `200` no-op regardless.)
 *
 * Mirrors the intent of Observatory's CancelRunConfirmDialog: cancellation is
 * graceful — running trials are left to drain — so the confirm copy says so.
 */

import { useCallback, useState } from 'react'
import type { CancelRunResult } from './board-client'

/** The interaction phases, one at a time. */
type CancelPhase = 'idle' | 'confirming' | 'cancelling' | 'cancelled'

export interface CancelRunButtonProps {
  /** The run to cancel. */
  runId: string
  /**
   * Fires the cancel. Injected (the parent binds the App's `fetchImpl` around
   * `cancelRun`) so the button is unit-testable without a network.
   */
  cancel: (runId: string) => Promise<CancelRunResult>
  /** Called once the cancel succeeds, so the parent can re-fetch the board. */
  onCancelled?: () => void
}

export function CancelRunButton({ runId, cancel, onCancelled }: CancelRunButtonProps): JSX.Element {
  const [phase, setPhase] = useState<CancelPhase>('idle')
  const [error, setError] = useState<string | null>(null)

  const confirm = useCallback(async () => {
    setPhase('cancelling')
    setError(null)
    try {
      await cancel(runId)
      // Optimistic: recede immediately, then let the parent re-fetch confirm the
      // run has left the live lanes so the gate unmounts this control for good.
      setPhase('cancelled')
      onCancelled?.()
    } catch (err) {
      // Revert to an actionable state and name the failure honestly.
      setError(err instanceof Error ? err.message : 'cancel failed')
      setPhase('idle')
    }
  }, [cancel, runId, onCancelled])

  // In-flight and the optimistic post-success window read the same: the control
  // has fired and is receding. Disabled and non-interactive either way.
  if (phase === 'cancelling' || phase === 'cancelled') {
    return (
      <span className="run-cancel run-cancel-pending" data-testid="cancel-run-pending" aria-live="polite">
        cancelling…
      </span>
    )
  }

  return (
    <span className="run-cancel">
      <button
        type="button"
        className="run-cancel-btn"
        data-testid="cancel-run-button"
        onClick={() => setPhase('confirming')}
      >
        Cancel
      </button>
      {error ? (
        <span className="run-cancel-error" role="alert" data-testid="cancel-run-error">
          {error}
        </span>
      ) : null}
      {phase === 'confirming' ? (
        <CancelConfirmDialog runId={runId} onConfirm={confirm} onDismiss={() => setPhase('idle')} />
      ) : null}
    </span>
  )
}

interface CancelConfirmDialogProps {
  runId: string
  onConfirm: () => void
  onDismiss: () => void
}

/** The graceful-cancel confirmation, mirroring Observatory's dialog intent. */
function CancelConfirmDialog({ runId, onConfirm, onDismiss }: CancelConfirmDialogProps): JSX.Element {
  return (
    <div className="cancel-dialog-root" data-testid="cancel-run-dialog">
      {/* Click-outside dismiss; a real <button> so it is keyboard-operable too. */}
      <button type="button" className="cancel-dialog-backdrop" aria-label="Dismiss" onClick={onDismiss} />
      {/* Native <dialog> (rendered `open`; React controls mount, not showModal)
          — implicit dialog role, so `useSemanticElements` is satisfied without a
          `role` attribute. The sibling backdrop above supplies the dimming +
          click-out; CSS resets the UA dialog defaults. */}
      <dialog className="cancel-dialog-panel" open aria-modal="true" aria-label={`Cancel run ${runId}`}>
        <p className="cancel-dialog-title">Cancel this run?</p>
        <p className="cancel-dialog-body">
          <code className="cancel-dialog-run">{runId}</code> stops admitting new trials and its queued work is
          cancelled. Trials already running are left to finish (graceful cancel). This cannot be undone.
        </p>
        <div className="cancel-dialog-actions">
          <button type="button" className="cancel-dialog-dismiss" data-testid="cancel-run-dismiss" onClick={onDismiss}>
            Keep running
          </button>
          <button type="button" className="cancel-dialog-confirm" data-testid="cancel-run-confirm" onClick={onConfirm}>
            Cancel run
          </button>
        </div>
      </dialog>
    </div>
  )
}
